// Vérification publique d'un document délivré par son code (GET /public/verification/:code) :
// type, collectivité, date, état et démarche, jamais de donnée personnelle. Le code QR imprimé
// sur le document ouvre cette page avec le code renseigné.
import { formaterFrancs } from '@cc/contrats';
import { Bouton, Champ, EtatVide, Formulaire, Squelette } from '@cc/ui';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErreurApi } from '../../../api/erreurs';
import { AffichageErreur } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useVerification } from '../api';
import { EnTeteCitoyen, TexteStatut } from '../commun';
import styles from '../citoyen.module.css';

const MOTIF_CODE = /^[A-Z0-9]{4}(-[A-Z0-9]{4}){3}$/;

/** Normalise un code saisi : majuscules, tirets tous les quatre caractères. */
export function normaliserCode(brut: string): string {
  const propre = brut.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
  return propre.replace(/(.{4})(?=.)/g, '$1-');
}

const LIBELLES_ETAT: Record<string, { libelle: string; ton: 'ok' | 'attention' | 'neutre' }> = {
  valide: { libelle: 'Document valide', ton: 'ok' },
  annule: { libelle: 'Document annulé par la collectivité', ton: 'attention' },
  elimine: { libelle: 'Document éliminé (durée de conservation échue)', ton: 'attention' },
};

function Resultat({ code }: { code: string }) {
  const v = useVerification(code);
  if (v.isPending) return <Squelette lignes={4} libelle="Vérification en cours" />;
  if (v.error instanceof ErreurApi && v.error.statut === 404) {
    return (
      <EtatVide titre="Aucun document ne correspond à ce code" icone={ShieldOff} niveau={2}>
        <p>
          Vérifiez la saisie du code <span className={styles.mono}>{code}</span>. Un document annulé ou un code inventé donnent la même réponse.
        </p>
      </EtatVide>
    );
  }
  if (v.error) return <AffichageErreur erreur={v.error} onReessayer={() => void v.refetch()} fonction="La vérification" />;
  const d = v.data;
  if (!d) return null;
  const etat = LIBELLES_ETAT[d.etat] ?? { libelle: d.etat, ton: 'neutre' as const };
  return (
    <section className={styles.carte} aria-labelledby="verification-titre">
      <h2 id="verification-titre" className={styles.blocTitre}>
        <ShieldCheck aria-hidden strokeWidth={1.75} width={20} height={20} /> <TexteStatut libelle={etat.libelle} ton={etat.ton} />
      </h2>
      <dl className={styles.definitions}>
        <dt>Code</dt>
        <dd className={styles.mono}>{code}</dd>
        <dt>Type de document</dt>
        <dd>{d.type_document}</dd>
        {d.demarche ? (
          <>
            <dt>Démarche</dt>
            <dd>{d.demarche}</dd>
          </>
        ) : null}
        {d.quittance ? (
          <>
            <dt>Quittance</dt>
            <dd className={styles.mono}>{d.quittance.numero}</dd>
            <dt>Rubrique</dt>
            <dd>{d.quittance.rubrique}</dd>
            <dt>Montant</dt>
            <dd>{formaterFrancs(d.quittance.montant)}</dd>
            {d.quittance.motif_annulation ? (
              <>
                <dt>Motif de l&apos;annulation</dt>
                <dd>{d.quittance.motif_annulation}</dd>
              </>
            ) : null}
          </>
        ) : null}
        <dt>Délivré par</dt>
        <dd>{d.collectivite}</dd>
        <dt>Date d&apos;émission</dt>
        <dd>{date(d.date_emission)}</dd>
      </dl>
      <p className={styles.aide}>Cette vérification ne révèle ni le nom du demandeur ni le contenu du document : elle confirme seulement qu&apos;il a été délivré par la collectivité.</p>
    </section>
  );
}

export function PageVerification() {
  const { code: codeAdresse } = useParams();
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState(codeAdresse ? normaliserCode(codeAdresse) : '');
  const [erreur, setErreur] = useState<string | null>(null);
  const codeValide = codeAdresse && MOTIF_CODE.test(normaliserCode(codeAdresse)) ? normaliserCode(codeAdresse) : null;

  const verifier = () => {
    const c = normaliserCode(saisie);
    if (!MOTIF_CODE.test(c)) {
      setErreur('Le code comporte seize lettres ou chiffres, en quatre groupes de quatre.');
      return;
    }
    setErreur(null);
    navigate(`/citoyen/verification/${c}`);
  };

  return (
    <>
      <EnTeteCitoyen
        titre="Vérifier un document"
        sousTitre="Saisissez le code imprimé sur l'autorisation, l'attestation ou la quittance. Le code QR du document ouvre directement cette page."
      />
      <Formulaire
        libelle="Vérification d'un document"
        onSoumettre={verifier}
        actions={
          <Bouton type="submit" pleineLargeur>
            Vérifier
          </Bouton>
        }
      >
        <Champ
          id="code-verification"
          libelle="Code de vérification"
          className={styles.mono}
          value={saisie}
          onChange={(e) => setSaisie(normaliserCode(e.target.value))}
          erreur={erreur}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          placeholder="XXXX-XXXX-XXXX-XXXX"
          required
        />
      </Formulaire>
      {codeValide ? <Resultat code={codeValide} /> : null}
    </>
  );
}
